import { supabase, resolveFunctionError } from '@/lib/supabase';

// Orden de tablas respetando las llaves foráneas: primero las "padres"
// (catálogo, contactos), luego las que dependen de ellas (pedidos, ventas,
// ítems y pagos). Restaurar fuera de este orden rompería las FKs.
export const RESTORE_ORDER: string[] = [
  'categories',
  'sizes',
  'products',
  'product_variants',
  'customers',
  'salespersons',
  'distributors',
  'suppliers',
  'campaigns',
  'discounts',
  'faqs',
  'testimonials',
  'donations',
  'expenses',
  'documents',
  'meta_pixel_events',
  'orders',
  'sales',
  'purchases',
  'consignments',
  'inventory',
  'inventory_movements',
  'order_items',
  'sale_items',
  'purchase_items',
  'consignment_items',
  'consignment_payments',
  'supplier_payments',
  'commissions',
  'warranties',
  'returns',
  'exchanges',
  'return_items',
  'exchange_items',
  'payments',
  'donation_entries',
  'campaign_metrics',
  'reservation_sweep_runs',
  'audit_logs',
];

export interface RestoreModule {
  id: string;
  label: string;
  icon: string;
  description: string;
  tables: string[];
}

// Agrupación amigable para que el admin elija por módulo en vez de por tabla.
export const RESTORE_MODULES: RestoreModule[] = [
  {
    id: 'catalog',
    label: 'Catálogo',
    icon: 'ri-store-2-line',
    description: 'Categorías, tallas, productos y sus variantes.',
    tables: ['categories', 'sizes', 'products', 'product_variants'],
  },
  {
    id: 'inventory',
    label: 'Inventario',
    icon: 'ri-stack-line',
    description: 'Stock disponible/reservado y trazabilidad de movimientos.',
    tables: ['inventory', 'inventory_movements'],
  },
  {
    id: 'contacts',
    label: 'Clientes y aliados',
    icon: 'ri-team-line',
    description: 'Clientes, vendedores, distribuidores y proveedores.',
    tables: ['customers', 'salespersons', 'distributors', 'suppliers'],
  },
  {
    id: 'orders',
    label: 'Pedidos',
    icon: 'ri-shopping-bag-3-line',
    description: 'Pedidos y sus ítems.',
    tables: ['orders', 'order_items'],
  },
  {
    id: 'sales',
    label: 'Ventas y facturación',
    icon: 'ri-receipt-line',
    description: 'Ventas, ítems, pagos y comisiones.',
    tables: ['sales', 'sale_items', 'payments', 'commissions'],
  },
  {
    id: 'after',
    label: 'Postventa',
    icon: 'ri-arrow-go-back-line',
    description: 'Devoluciones, cambios y garantías.',
    tables: ['warranties', 'returns', 'return_items', 'exchanges', 'exchange_items'],
  },
  {
    id: 'purchases',
    label: 'Compras',
    icon: 'ri-shopping-cart-2-line',
    description: 'Compras, ítems y abonos a proveedores.',
    tables: ['purchases', 'purchase_items', 'supplier_payments'],
  },
  {
    id: 'consignments',
    label: 'Consignaciones',
    icon: 'ri-git-repository-line',
    description: 'Consignaciones, mercancía y pagos de distribuidores.',
    tables: ['consignments', 'consignment_items', 'consignment_payments'],
  },
  {
    id: 'finances',
    label: 'Gastos y donaciones',
    icon: 'ri-bill-line',
    description: 'Gastos operativos y donaciones sociales.',
    tables: ['expenses', 'donations', 'donation_entries'],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: 'ri-megaphone-line',
    description: 'Campañas, métricas y eventos de Meta.',
    tables: ['campaigns', 'campaign_metrics', 'meta_pixel_events'],
  },
  {
    id: 'content',
    label: 'Contenido',
    icon: 'ri-article-line',
    description: 'Descuentos, preguntas frecuentes, testimonios y documentos.',
    tables: ['discounts', 'faqs', 'testimonials', 'documents'],
  },
  {
    id: 'system',
    label: 'Sistema',
    icon: 'ri-history-line',
    description: 'Barridos de reservas y auditoría.',
    tables: ['reservation_sweep_runs', 'audit_logs'],
  },
];

export interface ParsedBackup {
  version: string;
  timestamp: string;
  generatedBy: string;
  system: string;
  tables: Record<string, Record<string, unknown>[]>;
  counts: Record<string, number>;
  moduleCounts: Record<string, number>;
  totalRows: number;
}

export interface RestoreTableResult {
  table: string;
  count: number;
  error?: string;
}

export interface RestoreReport {
  success: boolean;
  restored: RestoreTableResult[];
  failed: RestoreTableResult[];
  error?: string;
}

// Lee y valida un archivo de respaldo (.json). Devuelve la estructura lista
// para la vista previa. Lanza un error legible si el archivo no es válido.
export async function parseBackupFile(file: File): Promise<ParsedBackup> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('El archivo no es un JSON válido.');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('El archivo no tiene una estructura de respaldo reconocible.');
  }

  const obj = parsed as Record<string, unknown>;
  const data = obj.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('El respaldo no contiene la sección "data". ¿Es un archivo de PCP?');
  }

  const tables: Record<string, Record<string, unknown>[]> = {};
  const counts: Record<string, number> = {};
  let totalRows = 0;

  for (const table of RESTORE_ORDER) {
    const value = (data as Record<string, unknown>)[table];
    if (Array.isArray(value)) {
      tables[table] = value as Record<string, unknown>[];
      counts[table] = value.length;
      totalRows += value.length;
    }
  }

  if (Object.keys(tables).length === 0) {
    throw new Error('El respaldo no contiene tablas reconocidas para restaurar.');
  }

  const moduleCounts: Record<string, number> = {};
  for (const mod of RESTORE_MODULES) {
    moduleCounts[mod.id] = mod.tables.reduce((sum, t) => sum + (counts[t] ?? 0), 0);
  }

  return {
    version: typeof obj.version === 'string' ? obj.version : '1.0',
    timestamp: typeof obj.timestamp === 'string' ? obj.timestamp : '',
    generatedBy: typeof obj.generated_by === 'string' ? obj.generated_by : 'desconocido',
    system: typeof obj.system === 'string' ? obj.system : 'PCP',
    tables,
    counts,
    moduleCounts,
    totalRows,
  };
}

// Restaura las tablas seleccionadas, en el orden seguro de FKs. Envía una
// petición por tabla (con bloques internos) y reporta el progreso.
export async function restoreTables(
  backup: ParsedBackup,
  tablesToRestore: string[],
  onProgress?: (done: number, total: number, table: string) => void,
): Promise<RestoreReport> {
  const restored: RestoreTableResult[] = [];
  const failed: RestoreTableResult[] = [];

  if (!supabase) {
    return { success: false, restored, failed, error: 'Sin conexión a la base de datos.' };
  }

  // Respeta el orden de FKs, solo con las tablas seleccionadas y presentes.
  const ordered = RESTORE_ORDER.filter(
    (t) => tablesToRestore.includes(t) && (backup.tables[t]?.length ?? 0) >= 0,
  );

  let done = 0;
  for (const table of ordered) {
    const rows = backup.tables[table] ?? [];
    onProgress?.(done, ordered.length, table);

    if (rows.length === 0) {
      restored.push({ table, count: 0 });
      done += 1;
      continue;
    }

    const { data, error } = await supabase.functions.invoke('restore-data', {
      body: { action: 'restore', table, rows },
    });

    if (error) {
      const message = await resolveFunctionError(error);
      failed.push({ table, count: 0, error: message });
    } else {
      const payload = data as { ok?: boolean; error?: string; count?: number } | null;
      if (!payload?.ok) {
        failed.push({ table, count: 0, error: payload?.error ?? 'No se pudo restaurar la tabla.' });
      } else {
        restored.push({ table, count: payload.count ?? rows.length });
      }
    }
    done += 1;
    onProgress?.(done, ordered.length, table);
  }

  return { success: failed.length === 0, restored, failed };
}